<template>
  <form
    ref="mainElement"
    class="sm:flex"
    @submit.prevent="onSubmit"
  >
    <div class="relative sm:flex sm:grow">
      <div
        ref="search-destination-container"
        class="relative sm:grow"
      >
        <label
          v-if="withSubmitButton"
          class="pointer-events-none absolute left-0 top-0 px-4 pt-3 text-xs uppercase text-gray-500"
          for="search-query-destination"
        >
          <slot name="destination-label">{{ $t('data.search.destination.label') }}</slot>
        </label>
        <!--
          Use pair value/input instead of v-model to avoid bug on android
          https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
        -->
        <input
          id="search-query-destination"
          :value="localDestination"
          type="text"
          name="destination"
          :placeholder="$t('data.search.destination.placeholder')"
          :class="[
            inputStyle,
            withDates ? 'rounded-t sm:rounded-l sm:rounded-tr-none' : 'rounded',
            {
              'placeholder:text-current focus:placeholder:text-gray-400': withSubmitButton,
            },
          ]"
          class="h-full w-full appearance-none truncate border border-gray-300 pr-11 outline-none"
          @input="(event) => (localDestination = (event.target as HTMLInputElement).value)"
          @focus="onInputDestinationfocus"
          @keypress.enter="onKeypress"
        >
        <YscIconsMarker
          :class="iconStyle"
          class="pointer-events-none absolute inset-y-0 right-0 mr-3 h-full text-gray-300"
        />
      </div>

      <div
        v-if="withDates"
        ref="search-dates-container"
        class="relative"
      >
        <span
          v-if="withSubmitButton"
          class="pointer-events-none absolute left-0 top-0 px-4 pt-3 text-xs uppercase text-gray-500"
        >
          <slot name="dates-label">{{ $t('data.search.dates.label') }}</slot>
        </span>

        <div
          v-if="withDates"
          class="-mt-px flex h-full w-full rounded-b border border-gray-300 bg-white pr-11 sm:-ml-px sm:mt-0 sm:rounded-r sm:rounded-bl-none"
          :class="[variant === 'tight' ? 'sm:w-64' : 'sm:w-72']"
        >
          <DatePicker
            v-model.range="form.dates"
            :locale="props.locale"
            :min-date="minDate"
            :columns="columns"
            color="pink"
            @popover-did-show="onDatePickerDidShow"
            @popover-did-hide="onDatePickerDidHide"
          >
            <template #default="{ togglePopover }">
              <button
                ref="datePickerToggleButton"
                type="button"
                :aria-labelledby="$t('data.search.dates.label')"
                :class="[inputStyle, !withSubmitButton && !hasDates ? 'text-gray-500' : 'text-current']"
                class="flex h-full w-full items-center whitespace-nowrap"
                @click="toggleDatePicker({ togglePopover })"
              >
                <template v-if="form.dates?.start && form.dates.end">
                  <span>{{ $d(form.dates.start, 'Y.numeric M.2digit D.numeric', props.locale) }}</span>
                  <YscIconsArrowRightLong class="mx-2 h-5 w-5" />
                  <span>{{ $d(form.dates.end, 'Y.numeric M.2digit D.numeric', props.locale) }}</span>
                </template>
                <template v-else>
                  {{ $t('data.search.dates.placeholder') }}
                </template>
              </button>
            </template>

            <template
              v-if="hasDates"
              #footer
            >
              <div class="flex justify-end px-4 pb-4">
                <button
                  class="link link-primary"
                  @click.prevent.stop="clearRange"
                >
                  {{ $t('commons.delete') }}
                </button>
              </div>
            </template>
          </DatePicker>
        </div>

        <YscIconsCalendar
          :class="iconStyle"
          class="pointer-events-none absolute inset-y-0 right-0 mr-3 h-full text-gray-300"
        />
      </div>

      <div
        v-if="hasResults"
        ref="results-container"
        class="absolute right-0 top-full z-50 mt-2 w-full rounded shadow-lg"
      >
        <div
          v-show="showDestinationResultsPopover"
          class="overflow-hidden rounded border border-gray-300 bg-white py-4"
        >
          <strong class="px-3 text-xs uppercase text-gray-500">{{ $t('commons.results') }}</strong>

          <div
            v-for="result in results"
            :key="result.locationId"
          >
            <input
              :id="result.locationId"
              type="checkbox"
              :value="result.locationId"
              class="peer absolute inset-0 -z-10 opacity-0"
              @change="onChangeLocalDestinationResult"
            >
            <label
              :for="result.locationId"
              class="peer-focus-visible:ring-peacock-300 relative flex w-full cursor-pointer items-center px-3 py-3 text-left leading-none hover:bg-gray-50 peer-focus-visible:ring-2"
            >
              {{ result.label }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="withSubmitButton"
      class="mt-4 sm:ml-4 sm:mt-0 sm:shrink-0"
    >
      <YscButtonSubmit
        :loading="loading"
        class="h-full w-full rounded-sm px-9 !text-lg"
      >
        {{ $t('commons.search') }}
      </YscButtonSubmit>
    </div>

    <YscModal
      v-if="isDestinationModal"
      fullscreen
      is-header-left-on-mobile
      @mounted="onDestinationModalMounted"
      @close="closeDestinationModal"
    >
      <template #title>
        {{ $t('commons.find_vehicle') }}
      </template>
      <div>
        <div
          ref="search-destination-container"
          class="relative sm:grow"
        >
          <!--
            Use pair value/input instead of v-model to avoid bug on android
            https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
            update: maybe resolved with vue3?
          -->
          <input
            ref="destinationsInput"
            :value="localDestination"
            type="text"
            name="destination"
            :placeholder="$t('data.search.destination.placeholder')"
            class="h-full w-full appearance-none truncate rounded border border-gray-300 px-4 py-3 pr-11 text-base outline-none placeholder:text-current focus:placeholder:text-gray-400"
            @input="(event) => (localDestination = (event.target as HTMLInputElement).value)"
            @keypress.enter="onKeypress"
          >
          <YscIconsMarker
            :class="iconStyle"
            class="pointer-events-none absolute inset-y-0 right-0 mr-3 h-full text-gray-300"
          />
        </div>
        <!-- search list -->
        <div class="mt-3">
          <span
            v-if="(!localDestination || localDestination === '') && results && results.length"
            class="text-sm text-gray-500"
          >
            {{ $t('data.search.recent') }}
          </span>
          <template v-if="results">
            <div
              v-for="result in results"
              :key="result.locationId"
              class="peer-focus-visible:ring-peacock-300 relative my-1 cursor-pointer rounded py-1 hover:bg-gray-50 peer-focus-visible:ring-2"
            >
              <input
                :id="result.locationId"
                type="checkbox"
                :value="result.locationId"
                class="peer absolute inset-0 -z-10 opacity-0"
                @click="onChangeLocalDestinationResult"
              >
              <label
                class="flex cursor-pointer items-center"
                :for="result.locationId"
              >
                <div class="mr-2 rounded bg-gray-100 p-2">
                  <YscIconsMarker class="h-6 w-6 text-gray-300" />
                </div>
                {{ result.label }}
              </label>
            </div>
          </template>
        </div>
      </div>
    </YscModal>
  </form>
</template>

<script setup lang="ts" generic="ResultsType extends {locationId: string, label: string}">
import { onClickOutside, useDebounceFn } from '@vueuse/core'
import { defineAsyncComponent, ref, watch, computed, useCalendarScreens } from '#imports'

const YscButtonSubmit = defineAsyncComponent(() => import('./YscButtonSubmit.vue'))
const YscIconsCalendar = defineAsyncComponent(() => import('./YscIconsCalendar.vue'))
const YscIconsMarker = defineAsyncComponent(() => import('./YscIconsMarker.vue'))
const YscIconsArrowRightLong = defineAsyncComponent(() => import('./YscIconsArrowRightLong.vue'))
const YscModal = defineAsyncComponent(() => import('./YscModal.vue'))

interface Props {
  destination?: string | null | { label: string }
  dateStart?: string | Date | null
  dateEnd?: string | Date | null
  results?: ResultsType[] // FIXME
  loading?: boolean
  withDates?: boolean
  withSubmitButton?: boolean
  variant?: 'tight' | 'relaxed' | null
  withDestinationModalMobile?: boolean
  locale?: string
}

const props = withDefaults(defineProps<Props>(), {
  destination: null,
  dateStart: null,
  dateEnd: null,
  results: () => [],
  loading: false,
  withDates: false,
  withSubmitButton: false,
  variant: null,
  withDestinationModalMobile: false,
  locale: 'fr',
})

type SearchForm = {
  destination: { locationId: string, label: string } | null
  dates: {
    start: Date | null
    end: Date | null
  } | null
}

const emit = defineEmits<{
  'dates-change': [SearchForm['dates']]
  'destination-change': [ResultsType | null]
  'submit': [{ form: SearchForm }]
  'destination-query-change': [string | null]
  'enter-key-pressed': []
}>()

const form = ref<SearchForm>({
  destination: null,
  dates: {
    start: null,
    end: null,
  },
})

const showDestinationResultsPopover = ref(false)
const hasResults = ref(false)

const onSubmit = () => {
  showDestinationResultsPopover.value = false
  emit('submit', { form: form.value })
}

const mainElement = ref<HTMLFormElement>()
onClickOutside(mainElement, () => {
  if (!showDestinationResultsPopover.value && !isDatePickerVisible.value) {
    return
  }
  showDestinationResultsPopover.value = false
  if (props.withDates && !props.withSubmitButton) {
    onSubmit()
  }
})

// --------------------------------------------------------------------------------------------
// DATES SELECTION
//

const { mapCurrent, screens } = useCalendarScreens()
const columns = mapCurrent({ 'lg': 2, 'xl': 2, '2xl': 2 }, 1)

const minDate = new Date()
if (props.dateStart && props.dateEnd) {
  const start = new Date(props.dateStart)
  const end = new Date(props.dateEnd)
  const latest = Math.max(start.getTime(), end.getTime())
  if (latest > minDate.getTime()) {
    const soonest = Math.max(Math.min(start.getTime(), end.getTime()), minDate.getTime())
    form.value.dates = {
      start: new Date(soonest),
      end: new Date(latest),
    }
  }
}

const hasDates = computed(() => {
  return !!form.value.dates?.start && !!form.value.dates?.end
})

const clearRange = () => {
  form.value.dates = { start: null, end: null }
}

watch(
  () => form.value.dates,
  (val) => {
    if (val?.start && val?.end && +val.start === +val?.end) {
      clearRange()
    }
    showDestinationResultsPopover.value = false
    emit('dates-change', val)
  },
  {
    immediate: true,
    deep: true,
  },
)

watch(
  () => props.results,
  () => {
    hasResults.value = props.results && props.results.length > 0
  },
)

const toggleDatePicker = ({ togglePopover }: { togglePopover?: (args: { showDelay: number, placement: string }) => void } = {}) => {
  showDestinationResultsPopover.value = false
  if (togglePopover) {
    togglePopover({ showDelay: 0, placement: 'bottom-end' })
  }
}

const isDatePickerVisible = ref(false)

const onDatePickerDidShow = () => {
  isDatePickerVisible.value = true
}
const onDatePickerDidHide = () => {
  isDatePickerVisible.value = false
}

const datePickerToggleButton = ref<HTMLButtonElement>()

// END DATES

// --------------------------------------------------------------------------------------------
// DESTINATION
//

const isDestinationModal = ref(false)

const destinationsInput = ref<HTMLInputElement>()

const onInputDestinationfocus = async () => {
  if (window.innerWidth < +screens.md.replace('px', '') && props.withDestinationModalMobile) {
    isDestinationModal.value = true
  }
  else {
    showDestinationResultsPopover.value = true
  }
}

const onDestinationModalMounted = () => {
  destinationsInput.value?.focus()
}

const emitDestinationQueryChange = useDebounceFn((val: string | null) => {
  emit('destination-query-change', val?.length ? val : null)
}, 350)

const localDestination = ref<string | null>('')
const localDestinationResult = ref<string | null>(null)

watch(
  () => props.destination,
  (val) => {
    if (val === null || typeof val === 'string') {
      localDestination.value = val
      return
    }

    localDestination.value = val?.label ?? null
  },
  {
    immediate: true,
  },
)

watch(
  () => localDestination.value,
  (val) => {
    if (form.value.destination === null || form.value.destination.label !== val) {
      emitDestinationQueryChange(val)
    }

    if (!val?.length) {
      form.value.destination = null
    }
  },
)

const onKeypress = (event: KeyboardEvent) => {
  if (event.keyCode === 13) {
    event.preventDefault()
    emit('enter-key-pressed')
    isDestinationModal.value = false
  }
}

const onChangeLocalDestinationResult = ({ target }: Event) => {
  showDestinationResultsPopover.value = false

  if (isDestinationModal.value) {
    isDestinationModal.value = false
  }

  if (datePickerToggleButton.value && !hasDates.value) {
    datePickerToggleButton.value.click()
  }

  const value = target && target instanceof HTMLInputElement ? target.value : null

  if (localDestinationResult.value !== value) {
    localDestinationResult.value = value
    const selected = props.results.find(({ locationId }) => value === locationId) || null
    form.value.destination = selected
    localDestination.value = selected !== null ? selected.label : null
    emit('destination-change', selected)
  }
}

const closeDestinationModal = () => {
  isDestinationModal.value = false
  localDestination.value = null
  localDestinationResult.value = null
}

// --------------------------------------------------------------------------------------------
// STYLING
//

const inputStyle = computed(() => {
  if (props.withSubmitButton) {
    return 'px-4 pb-3 pt-8 text-base'
  }
  switch (props.variant) {
    case 'tight':
      return 'py-2 px-3 text-sm'
    case 'relaxed':
      return 'p-4 text-normal'
    default:
      return 'py-3 px-4 text-base'
  }
})

const iconStyle = computed(() => {
  if (props.variant === 'tight') {
    return 'w-5'
  }
  return 'w-6'
})
</script>
